/**
 * D6 determinism: "the keymap after any mutation equals the keymap a reload
 * would compute from the persisted state".
 *
 * One `validateKeymap` pass is not enough for that. Its `overridesPatch` can
 * turn a displaced command's remaining scheme chords into a user override,
 * which raises their collision precedence — so another collision may resolve
 * differently on the next validation. This module therefore:
 *  1. validates with the mutation (its added chords win) and applies the
 *     patch, repeating until the patch is empty;
 *  2. then validates the final overrides WITHOUT the mutation — exactly what
 *     hydration does on the next start — and takes the keymap and the
 *     non-durable notices from that pass.
 * Durable notices (displaced chords, refused reserved chords) from every pass
 * are kept, so the Settings tab can still offer Undo for them.
 */
import { validateKeymap } from '../model/validateKeymap';
import type {
  KeybindingCommandId,
  KeybindingNotice,
  KeybindingScheme,
  KeyboardLayoutSnapshot,
  Keymap,
  KeymapValidationResult,
  Platform,
} from '../model/keybindingTypes';
import type { KeybindingOverrideMap } from './keybindingsStoreTypes';

/** Enough for any real chain (each pass only removes chords); reaching it means a bug. */
export const MAX_SETTLE_PASSES = 8;

export type SettleContext = {
  readonly scheme: KeybindingScheme;
  readonly platform: Platform;
  readonly layoutMap: KeyboardLayoutSnapshot;
  readonly isDev: boolean;
};

export type SettleMutation = {
  readonly mutatedCommandId?: KeybindingCommandId;
  readonly mutatedChords?: readonly string[];
  /** The keymap before the mutation (O1 ii): only a default chord that was active can be written as a loser. */
  readonly previousKeymap?: Keymap;
};

export type SettledKeymap = {
  /** The final pass: validation of `overrides` with no mutation, as a reload would run it. */
  readonly result: KeymapValidationResult;
  readonly overrides: KeybindingOverrideMap;
  /** Durable notices produced by any pass, de-duplicated. */
  readonly durableNotices: readonly KeybindingNotice[];
  readonly passes: number;
  readonly converged: boolean;
};

export function sameNotice(left: KeybindingNotice, right: KeybindingNotice): boolean {
  return left.kind === right.kind && left.commandId === right.commandId &&
    left.chord === right.chord && left.relatedCommandId === right.relatedCommandId &&
    left.persisted === right.persisted && left.message === right.message && left.origin === right.origin;
}

export function applyPatch(
  overrides: KeybindingOverrideMap,
  patch: Partial<Record<KeybindingCommandId, readonly string[]>>,
): KeybindingOverrideMap {
  const patchedIds = Object.keys(patch) as KeybindingCommandId[];
  if (patchedIds.length === 0) return overrides;
  const next: Record<string, readonly string[]> = { ...overrides };
  for (const id of patchedIds) {
    const chords = patch[id];
    if (chords) next[id] = [...chords];
  }
  return next;
}

/**
 * Dev builds only — not packaged builds (DEV is false) and not unit tests
 * (MODE 'test'), where the property tests assert convergence instead (FR6).
 */
function warnUnsettled(passes: number): void {
  if (!import.meta.env.DEV || import.meta.env.MODE === 'test') return;
  console.warn(`[keybindings] settleKeymap did not converge after ${passes} passes; `
    + 'the committed keymap may differ from the one a reload computes.');
}

export function settleKeymap(
  initialOverrides: KeybindingOverrideMap,
  context: SettleContext,
  mutation: SettleMutation = {},
): SettledKeymap {
  let overrides = initialOverrides;
  let pending: SettleMutation | null = mutation.mutatedCommandId ? mutation : null;
  const durableNotices: KeybindingNotice[] = [];
  const validate = (): KeymapValidationResult => validateKeymap({ ...context, overrides, ...(pending ?? {}) });

  for (let pass = 1; pass <= MAX_SETTLE_PASSES; pass += 1) {
    const result = validate();
    for (const notice of result.notices) {
      if (notice.persisted && !durableNotices.some((kept) => sameNotice(kept, notice))) durableNotices.push(notice);
    }
    const next = applyPatch(overrides, result.overridesPatch);
    if (next !== overrides) {
      overrides = next;
      continue;
    }
    if (pending) {
      // The mutation has settled; now compute what a reload would.
      pending = null;
      continue;
    }
    return { result, overrides, durableNotices, passes: pass, converged: true };
  }
  pending = null;
  warnUnsettled(MAX_SETTLE_PASSES);
  return { result: validate(), overrides, durableNotices, passes: MAX_SETTLE_PASSES, converged: false };
}
